import {
  Agreement,
  SubmitApplicationResultTypenames,
} from '@varo/graphql-schema-types';
import { useRouter } from 'expo-router';
import { NAV_ANIMATION_DURATION } from 'features/application/constants';
import { useApplicationTrackErrorEvents } from 'features/application/hooks/useApplicationTrackErrorEvents';
import { useEffect, useState } from 'react';
import SubmitBankApplication from 'services/graphql/mutations/SubmitBankApplication';
import useCancellableMutation from 'shared/hooks/useCancellableMutation';
import { getUuid } from 'shared/utils/getUuid/getUuid';

interface SubmitApplicationResponse {
  data: {
    submitApplication: {
      __typename: string;
      message?: string;
    };
  };
}

interface UseSubmitApplicationArgs {
  applicationId?: string;
  sourceName?: string;
}

export interface SubmitApplicationArgs {
  agreements?: Agreement[];
  isReapplication?: boolean;
}

const useSubmitApplication = ({
  applicationId,
  sourceName,
}: UseSubmitApplicationArgs = {}) => {
  const router = useRouter();
  const [isSubmitApplicationLoading, setIsSubmitApplicationLoading] =
    useState<boolean>(false);
  const [isSubmitApplicationSuccess, setIsSubmitApplicationSuccess] =
    useState<boolean>(false);
  const [submitApplicationErrorMessage, setSubmitApplicationErrorMessage] =
    useState<string>('');
  const { logErrorEvent } = useApplicationTrackErrorEvents();

  const [submitApplicationMutationResult, submitApplicationExecuteMutation] =
    useCancellableMutation(SubmitBankApplication);

  const submitApplicationError = submitApplicationMutationResult.error;

  useEffect(() => {
    if (sourceName && submitApplicationError) {
      const errorMessage = submitApplicationError.message;
      logErrorEvent(errorMessage, sourceName);
      setSubmitApplicationErrorMessage(errorMessage);
    }
  }, [logErrorEvent, sourceName, submitApplicationError]);

  const submitApplication = async ({
    agreements,
    isReapplication,
  }: SubmitApplicationArgs) => {
    setIsSubmitApplicationLoading(true);

    const response = (await submitApplicationExecuteMutation({
      idempotencyId: getUuid(),
      applicationId,
      agreements: agreements?.map((agreement: Agreement) => agreement.id),
    })) as SubmitApplicationResponse;

    const submissionResult = response?.data?.submitApplication;

    if (
      submissionResult?.__typename ===
      SubmitApplicationResultTypenames.SubmitApplicationSuccess
    ) {
      setIsSubmitApplicationSuccess(true);

      setTimeout(() => {
        router.replace({
          pathname: '/signup/application-decision',
          params: {
            sourceName,
            ...(isReapplication && {
              isReapplicationFlow: 'true',
            }),
          },
        });

        setIsSubmitApplicationSuccess(false);
      }, NAV_ANIMATION_DURATION);
    } else if (
      submissionResult?.__typename ===
      SubmitApplicationResultTypenames.SubmitApplicationError
    ) {
      if (sourceName && submissionResult.message) {
        logErrorEvent(submissionResult.message, sourceName);
        setSubmitApplicationErrorMessage(submissionResult.message);
      }
    }

    setTimeout(() => {
      setIsSubmitApplicationLoading(false);
      // This is to prevent flash of error screen before navigation to onboarding video
    }, NAV_ANIMATION_DURATION + 30);
  };

  return {
    submitApplication,
    isSubmitApplicationSuccess,
    isSubmitApplicationLoading,
    submitApplicationMutationResult,
    submitApplicationErrorMessage,
  };
};

export default useSubmitApplication;
